import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "../../api/useApiClient";
import { ALERT_RULES_QUERY_KEY } from "../../api/hooks/useAlertRules";
import { PredicateBuilderDialog } from "../predicates/PredicateBuilderDialog";

/**
 * AlertRule builder dialog -- thin wrapper around the shared
 * `PredicateBuilderDialog`. Mirrors `ScreenBuilderDialog` and reuses
 * the same field / op / value picker since AlertRule + SavedScreen
 * share `ScreenCriteria` per V2-2-U4a Q-AST.
 *
 * The wrapped mutation adapts `useCreateAlertRule`'s body type
 * (which carries an optional `active: bool` field defaulted to True
 * server-side) to the predicate-dialog's `{ name, criteria }` shape;
 * new rules ship `active=true` implicitly, matching the inbox
 * UX expectation that a freshly created rule should fire on the next
 * matching trade-close.
 */
export type AlertRuleBuilderDialogProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function AlertRuleBuilderDialog({
  open,
  onOpenChange,
}: AlertRuleBuilderDialogProps = {}) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation<
    unknown,
    Error,
    { name: string; criteria: Record<string, unknown> }
  >({
    mutationFn: async (body) => {
      const { data, error } = await client.POST("/api/alerts/rules", {
        body: { name: body.name, criteria: body.criteria, active: true },
      });
      if (error !== undefined) {
        throw new Error("Failed to create alert rule");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ALERT_RULES_QUERY_KEY });
    },
  });

  return (
    <PredicateBuilderDialog
      open={open}
      onOpenChange={onOpenChange}
      triggerLabel="+ New rule"
      dialogAriaLabel="Create alert rule"
      title="Create alert rule"
      description="A rule fires once per matching trade close. Combine conditions with AND / OR / NOT."
      nameLabel="Rule name"
      idPrefix="create-alert-rule"
      errorMessage="Failed to save rule. Check the field / operator / value."
      submitLabel="Create rule"
      mutation={mutation}
    />
  );
}
